import * as React from 'react';

import { StackProps } from '@mui/material/Stack';
import { format } from 'date-fns';
import { first, toUpper, uniqueId } from 'lodash';

import { FacilityOpenshiftEmployeeResponse } from '@sbv-clients/data-access-api';
import {
  Avatar,
  Box,
  CustomBadge,
  getGridBooleanOperators,
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
  ShiftIconTitle,
  Stack,
  StatusTag,
  theme,
  Typography,
  Icon,
} from '@sbv-clients/ui-shared';
import { nameOfFactory } from '@sbv-clients/util-misc';

import { isWeekendOperator } from './use-applied-filter';

export interface RegularUniqueSum {
  regular: number;
  unique: number;
}

export interface EmployeesRowType extends FacilityOpenshiftEmployeeResponse {}

export const employeesRowKey = nameOfFactory<EmployeesRowType>();

export const getEmployeesRowId = (row: GridValidRowModel) =>
  ((row as EmployeesRowType)?.shiftId || uniqueId()) +
  ((row as EmployeesRowType)?.employeeId || uniqueId());

const Container = (props: StackProps) => {
  return (
    <Stack
      spacing={2}
      direction={'row'}
      lineHeight="20px"
      height="100%"
      alignItems="center"
      {...props}
    />
  );
};

export const employeeColumns: GridColDef[] = [
  {
    flex: 2,
    headerName: 'Name',
    field: employeesRowKey('fullName'),
    renderCell: (params) =>
      params.value === undefined ? undefined : (
        <Stack
          spacing={2}
          direction={'row'}
          alignItems={'center'}
          height="100%"
        >
          <Avatar sx={{ height: 24, width: 24 }}>
            {toUpper(first(params.value))}
          </Avatar>
          <Typography>{params.value}</Typography>
        </Stack>
      ),
  },
  {
    flex: 1,
    headerName: 'Employee ID',
    field: employeesRowKey('employeeId'),
  },
  {
    flex: 1,
    headerName: 'Instance Employee ID',
    field: employeesRowKey('instanceEmployeeId'),
  },
  {
    flex: 1,
    headerName: 'Shift',
    field: employeesRowKey('shiftSchedulerId'),
    renderCell: (params) =>
      params.value === undefined ? undefined : (
        <ShiftIconTitle value={params.value} />
      ),
  },
  {
    flex: 1,
    headerName: 'Shift Date',
    field: employeesRowKey('startDateTime'),
    type: 'date',
    valueFormatter: (value) =>
      value ? format(value, 'EEE, MMM dd') : undefined,
    valueGetter: (value) => (value ? new Date(value) : undefined),
    filterOperators: [isWeekendOperator],
  },
  {
    flex: 1,
    headerName: 'Start',
    field: 'shiftStartTime',
    valueGetter: (value, row: GridValidRowModel) =>
      row.startDateTime
        ? format(new Date(row.startDateTime), 'h:mm a')
        : undefined,
  },
  {
    flex: 1,
    headerName: 'End',
    field: 'shiftEndTime',
    valueGetter: (value, row: GridValidRowModel) =>
      row.endDateTime ? format(new Date(row.endDateTime), 'h:mm a') : undefined,
  },
  {
    flex: 1.5,
    headerName: 'Notified',
    field: employeesRowKey('notified'),
    renderCell: (
      params: GridRenderCellParams<EmployeesRowType & RegularUniqueSum>
    ) => {
      return (
        <Container>
          <CustomBadge type={'secondary'} darken small>
            {params.aggregation ? params.value.regular : params.value}
          </CustomBadge>
          <CustomBadge type={'info'} small>
            {params.aggregation
              ? params.value.unique
              : params.row.uniqueNotified}{' '}
            Unique
          </CustomBadge>
        </Container>
      );
    },
    availableAggregationFunctions: ['sumBothNotified'],
  },
  {
    flex: 1,
    headerName: 'Accepted',
    field: employeesRowKey('interested'),
    renderCell: (params) => (
      <Container>
        <CustomBadge type={'secondary'} darken>
          {params.value}
        </CustomBadge>
      </Container>
    ),
    availableAggregationFunctions: ['sum'],
  },
  {
    flex: 1,
    headerName: 'Dismissed',
    field: employeesRowKey('dismissed'),
    renderCell: (params) => (
      <Container>
        <CustomBadge type={'secondary'} darken sx={{}}>
          {params.value}
        </CustomBadge>
      </Container>
    ),
    availableAggregationFunctions: ['sum'],
  },
  {
    flex: 1.5,
    headerName: 'Assigned',
    field: employeesRowKey('assigned'),
    renderCell: (params: GridRenderCellParams<EmployeesRowType>) => (
      <Container>
        <CustomBadge type={'secondary'} darken>
          {params.aggregation ? params.value.regular : params.value}
        </CustomBadge>
        <CustomBadge type={'info'}>
          {params.aggregation ? params.value.unique : params.row.uniqueAssigned}{' '}
          Unique
        </CustomBadge>
      </Container>
    ),
    availableAggregationFunctions: ['sumBothAssigned'],
  },
  {
    flex: 1,
    headerName: 'Active/Inactive',
    field: employeesRowKey('isActive'),
    renderCell: (params) => {
      return (
        <Container>
          {params.aggregation ? (
            <Typography>
              {params.value.top}/{params.value.bottom}
            </Typography>
          ) : (
            <StatusTag tone={params.value ? 'success' : 'neutral'}>
              {params.value ? 'Active' : 'Inactive'}
            </StatusTag>
          )}
        </Container>
      );
    },
    availableAggregationFunctions: ['ratio'],
  },
  {
    flex: 1,
    headerName: 'Onboarded',
    field: employeesRowKey('isOnboarded'),
    renderCell: (params) => {
      return (
        <Container>
          {params.aggregation ? (
            <Typography>
              {params.value.top}/{params.value.bottom}
            </Typography>
          ) : (
            <StatusTag tone={params.value ? 'success' : 'neutral'}>
              {params.value ? 'Yes' : 'No'}
            </StatusTag>
          )}
        </Container>
      );
    },
    availableAggregationFunctions: ['ratio'],
    filterOperators: getGridBooleanOperators(),
  },
  {
    flex: 1,
    headerName: 'Registered',
    field: employeesRowKey('isRegistered'),
    renderCell: (params) => {
      return (
        <Container>
          {params.aggregation ? (
            <Typography>
              {params.value.top}/{params.value.bottom}
            </Typography>
          ) : (
            <StatusTag tone={params.value ? 'success' : 'neutral'}>
              {params.value ? 'Yes' : 'No'}
            </StatusTag>
          )}
        </Container>
      );
    },
    availableAggregationFunctions: ['ratio'],
    filterOperators: getGridBooleanOperators(),
  },
  {
    flex: 0.3,
    headerName: '',
    renderCell: (
      params: GridRenderCellParams<
        FacilityOpenshiftEmployeeResponse,
        FacilityOpenshiftEmployeeResponse['saasRole']
      >
    ) => {
      return params.value === undefined ? undefined : (
        <Box sx={{ pr: 2, pt: 2, color: theme.text.secondary }}>
          {!params.value ? (
            <Icon name={'DesktopWindowsOutlined'} fontSize={'small'} />
          ) : (
            <Icon name={'PhoneIphoneOutlined'} fontSize={'small'} />
          )}
        </Box>
      );
    }, //   <Button endIcon={} >Employees</Button>
    align: 'right',
    field: employeesRowKey('saasRole'),
  },
];
